import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { imageUrl } from '../lib/images';

export default function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    totalItems,
    totalPrice,
    hasPrices,
  } = useCart();

  const [step, setStep] = useState('cart'); // 'cart' | 'checkout' | 'success'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [orderReceipt, setOrderReceipt] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    delivery_method: 'levering', // 'levering' | 'afhentning'
    address: '',
    zip: '',
    city: '',
    notes: '',
    website_url: '', // Mandatory honeypot
  });

  if (!isCartOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStartCheckout = () => {
    setErrorMessage('');
    setStep('checkout');
  };

  const handleBackToCart = () => {
    setErrorMessage('');
    setStep('cart');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Antispam Honeypot check
    if (formData.website_url) {
      console.warn('Bot detected via honeypot');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      setErrorMessage('Udfyld venligst navn, e-mail og telefonnummer.');
      return;
    }

    if (formData.delivery_method === 'levering' && (!formData.address || !formData.zip || !formData.city)) {
      setErrorMessage('Udfyld venligst leveringsadresse, postnummer og by.');
      return;
    }

    if (items.length === 0) {
      setErrorMessage('Din kurv er tom.');
      return;
    }

    setIsSubmitting(true);

    try {
      const orderNumber = `KRO-${Math.floor(100000 + Math.random() * 900000)}`;

      // Format readable text summary of all products
      const productsSummary = items
        .map(
          (it, idx) =>
            `${idx + 1}. ${it.name} (${it.variant}) - Antal: ${it.quantity} stk.${
              it.price ? ` - a ${it.price} kr. (Linje: ${it.price * it.quantity} kr.)` : ''
            }`
        )
        .join('\n');

      const deliveryText =
        formData.delivery_method === 'levering'
          ? `Levering til: ${formData.address}, ${formData.zip} ${formData.city}`
          : 'Afhentning hos KROMIX / Forhandler';

      const fullSummary = `BESTILLINGSFORMULAR / WEBSHOP (UDEN BETALING)\nOrdrenummer: ${orderNumber}\n\nKUNDE:\nNavn: ${formData.name}\nEmail: ${formData.email}\nTelefon: ${formData.phone}${
        formData.company ? `\nFirma/CVR: ${formData.company}` : ''
      }\n\nLEVERING:\n${deliveryText}\n\nBESTILTE VARER (${totalItems} stk):\n${productsSummary}\n\nSamlet vejledende beløb: ${
        totalPrice > 0 ? `${totalPrice} DKK inkl. moms` : 'Aftales ved bekræftelse'
      }\n\nBEMÆRKNINGER:\n${formData.notes || 'Ingen bemærkninger angivet.'}`;

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company || '',
        delivery_method: formData.delivery_method,
        address: formData.address || '',
        zip: formData.zip || '',
        city: formData.city || '',
        notes: formData.notes || '',
        order_number: orderNumber,
        order_total_items: totalItems,
        order_total_price: totalPrice > 0 ? `${totalPrice} kr.` : 'Tilbud/aftales',
        order_summary: fullSummary,
        order_items_json: JSON.stringify(items),
        website_url: formData.website_url || '', // Mandatory Honeypot
        subject: `Ny bestilling #${orderNumber} fra ${formData.name} (${totalItems} varer)`,
      };

      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Kunne ikke indsende bestillingen. Prøv venligst igen eller ring til os.');
      }

      // Save order receipt for confirmation screen
      setOrderReceipt({
        orderNumber,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        items: [...items],
        totalItems,
        totalPrice,
        deliveryMethod: formData.delivery_method,
        deliveryAddress:
          formData.delivery_method === 'levering'
            ? `${formData.address}, ${formData.zip} ${formData.city}`
            : 'Afhentning hos KROMIX',
      });

      // Clear cart
      clearCart();
      setStep('success');
    } catch (err) {
      console.error('Bestillingsfejl:', err);
      setErrorMessage(
        err.message || 'Der opstod en fejl under afsendelsen. Ring venligst til 75 73 18 61.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAndReset = () => {
    closeCart();
    // Reset view after animation
    setTimeout(() => {
      setStep('cart');
      setErrorMessage('');
      setOrderReceipt(null);
    }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#252927]/60 backdrop-blur-xs transition-opacity duration-300"
        onClick={handleCloseAndReset}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div className="fixed inset-y-0 right-0 max-w-full flex pl-6 sm:pl-10">
        <div className="w-screen max-w-lg bg-[#FCFAF5] shadow-2xl flex flex-col border-l border-[#DED3C0]">
          {/* Header */}
          <div className="px-6 py-5 bg-[#39463A] text-white flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#273228] flex items-center justify-center text-[#BCC6B2]">
                <i className="fa-solid fa-basket-shopping text-lg" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                  <span>
                    {step === 'cart' && 'Din Bestillingskurv'}
                    {step === 'checkout' && 'Bestil uden betaling'}
                    {step === 'success' && 'Bestilling modtaget!'}
                  </span>
                  {step === 'cart' && totalItems > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-[#71806A] text-[11px] font-bold">
                      {totalItems} {totalItems === 1 ? 'vare' : 'varer'}
                    </span>
                  )}
                </h2>
                <p className="text-[11px] text-[#DED3C0]">
                  Webshop uden direkte kortbetaling – vi kontakter dig
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleCloseAndReset}
              className="w-9 h-9 rounded-xl bg-[#273228]/80 hover:bg-[#273228] text-[#DED3C0] hover:text-white flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Luk kurv"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Subheader Notice for Webshop model */}
          <div className="bg-[#F4EFE5] px-6 py-2.5 border-b border-[#DED3C0] flex items-center gap-2 text-xs text-[#39463A]">
            <i className="fa-solid fa-shield-check text-[#71806A]" aria-hidden="true" />
            <span className="font-medium">
              100% uforpligtende afsendelse – afregning via faktura/MobilePay
            </span>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {/* STEP 1: CART OVERVIEW */}
            {step === 'cart' && (
              <>
                {items.length === 0 ? (
                  <div className="py-20 text-center flex flex-col items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[#F4EFE5] border border-[#DED3C0] flex items-center justify-center text-[#71806A]/50 mb-4">
                      <i className="fa-light fa-basket-shopping text-3xl" aria-hidden="true" />
                    </div>
                    <h3 className="text-base font-bold text-[#39463A]">Din kurv er tom</h3>
                    <p className="text-xs text-[#39463A]/70 mt-1 max-w-xs">
                      Gå på opdagelse i vores sortiment af hundefoder, kattefoder og tilbehør.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-2">
                      <Link
                        to="/hundefoder"
                        onClick={closeCart}
                        className="px-5 py-2.5 rounded-xl bg-[#71806A] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#5C6A56] transition-colors"
                      >
                        Se hundefoder
                      </Link>
                      <Link
                        to="/diverse"
                        onClick={closeCart}
                        className="px-5 py-2.5 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#39463A] text-xs font-bold uppercase tracking-wider hover:bg-[#DED3C0] transition-colors"
                      >
                        Se tilbehør
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-2 border-b border-[#DED3C0]">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                        Valgte produkter ({totalItems})
                      </span>
                      <button
                        type="button"
                        onClick={clearCart}
                        className="text-[11px] font-semibold text-[#B98262] hover:text-[#9A6447] transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <i className="fa-regular fa-trash-can" aria-hidden="true" />
                        <span>Tøm kurv</span>
                      </button>
                    </div>

                    {/* Products List */}
                    <ul className="divide-y divide-[#DED3C0]">
                      {items.map((item) => (
                        <li key={item.id} className="py-4 flex gap-3 sm:gap-4 items-center">
                          {/* Image */}
                          <div className="w-18 h-18 sm:w-20 sm:h-20 shrink-0 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] p-2 flex items-center justify-center">
                            {item.image ? (
                              <img
                                src={imageUrl(item.image, 'width=160,quality=80,format=auto')}
                                alt={item.name}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <i className="fa-light fa-box text-2xl text-[#71806A]/40" aria-hidden="true" />
                            )}
                          </div>

                          {/* Info & Quantity */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h4 className="text-sm font-bold text-[#39463A] leading-snug line-clamp-1">
                                  {item.slug ? (
                                    <Link
                                      to={`/produkter/${item.slug}`}
                                      onClick={closeCart}
                                      className="hover:text-[#71806A] transition-colors"
                                    >
                                      {item.name}
                                    </Link>
                                  ) : (
                                    item.name
                                  )}
                                </h4>
                                <p className="text-[11px] text-[#71806A] font-medium mt-0.5">
                                  Variant: <span className="text-[#39463A]">{item.variant}</span>
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFromCart(item.id)}
                                className="text-[#B98262] hover:text-[#9A6447] p-1 text-xs cursor-pointer"
                                aria-label={`Fjern ${item.name}`}
                              >
                                <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
                              </button>
                            </div>

                            {/* Controls: [- 1 +] and Price */}
                            <div className="flex items-center justify-between mt-3 pt-1">
                              {/* Quantity Selector */}
                              <div className="inline-flex items-center rounded-lg bg-[#F4EFE5] border border-[#DED3C0] p-0.5">
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="w-7 h-7 flex items-center justify-center text-[#39463A] hover:bg-[#DED3C0]/60 rounded text-xs transition-colors cursor-pointer"
                                  aria-label="Træk en fra"
                                >
                                  <i className="fa-solid fa-minus text-[10px]" aria-hidden="true" />
                                </button>
                                <span className="w-8 text-center text-xs font-bold text-[#39463A]">
                                  {item.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  className="w-7 h-7 flex items-center justify-center text-[#39463A] hover:bg-[#DED3C0]/60 rounded text-xs transition-colors cursor-pointer"
                                  aria-label="Læg en til"
                                >
                                  <i className="fa-solid fa-plus text-[10px]" aria-hidden="true" />
                                </button>
                              </div>

                              {/* Price */}
                              <div className="text-right">
                                {item.price ? (
                                  <div>
                                    <span className="text-sm font-extrabold text-[#39463A]">
                                      {item.price * item.quantity} kr.
                                    </span>
                                    {item.quantity > 1 && (
                                      <span className="block text-[10px] text-[#71806A]">
                                        ({item.price} kr. / stk.)
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs font-semibold text-[#71806A]">
                                    Pris på forespørgsel
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* How It Works Card */}
                    <div className="p-4 rounded-2xl bg-[#F4EFE5] border border-[#DED3C0] space-y-2 mt-6">
                      <div className="flex items-center gap-2 text-xs font-bold text-[#39463A]">
                        <i className="fa-solid fa-circle-question text-[#71806A]" aria-hidden="true" />
                        <span>Hvordan fungerer webshoppen uden betaling?</span>
                      </div>
                      <p className="text-[11px] text-[#39463A]/80 leading-relaxed">
                        Du tilføjer blot de ønskede foderprodukter og sender bestillingen. Vi pakker ordren, bekræfter levering/afhentning og sender faktura eller MobilePay-anmodning. Ingen kreditkortoplysninger skal indtastes her.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* STEP 2: CHECKOUT WEBFORM */}
            {step === 'checkout' && (
              <form onSubmit={handleSubmitOrder} className="space-y-5">
                <div className="flex items-center justify-between pb-2 border-b border-[#DED3C0]">
                  <button
                    type="button"
                    onClick={handleBackToCart}
                    className="text-xs font-bold text-[#71806A] hover:text-[#39463A] flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <i className="fa-solid fa-arrow-left text-[11px]" aria-hidden="true" />
                    <span>Tilbage til kurv ({totalItems} varer)</span>
                  </button>
                  <span className="text-[11px] font-semibold text-[#39463A]/70">
                    Trin 2 af 2
                  </span>
                </div>

                {errorMessage && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
                    <i className="fa-solid fa-triangle-exclamation text-sm shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Mandatory Honeypot Anti-Spam (Rule 12) */}
                <div className="sr-only" aria-hidden="true">
                  <label htmlFor="drawer_website_url">Website URL</label>
                  <input
                    type="text"
                    id="drawer_website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Delivery Option Toggle */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#71806A] block">
                    Leveringsmetode *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, delivery_method: 'levering' }))}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                        formData.delivery_method === 'levering'
                          ? 'bg-[#39463A] text-white border-[#39463A] shadow-xs'
                          : 'bg-[#F4EFE5] text-[#39463A] border-[#DED3C0] hover:border-[#71806A]'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <i className="fa-solid fa-truck-fast" aria-hidden="true" />
                        <span>Levering</span>
                      </div>
                      <span className="block text-[10px] mt-1 opacity-80">
                        Direkte til adressen
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, delivery_method: 'afhentning' }))}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                        formData.delivery_method === 'afhentning'
                          ? 'bg-[#39463A] text-white border-[#39463A] shadow-xs'
                          : 'bg-[#F4EFE5] text-[#39463A] border-[#DED3C0] hover:border-[#71806A]'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <i className="fa-solid fa-warehouse" aria-hidden="true" />
                        <span>Afhentning</span>
                      </div>
                      <span className="block text-[10px] mt-1 opacity-80">
                        KROMIX el. forhandler
                      </span>
                    </button>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-3 pt-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A] block">
                    Kontaktoplysninger
                  </span>

                  <div>
                    <label htmlFor="drawer_name" className="block text-xs font-bold text-[#39463A] mb-1">
                      Fulde navn *
                    </label>
                    <input
                      type="text"
                      id="drawer_name"
                      name="name"
                      required
                      placeholder="F.eks. Anders Hansen"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="drawer_email" className="block text-xs font-bold text-[#39463A] mb-1">
                        E-mailadresse *
                      </label>
                      <input
                        type="email"
                        id="drawer_email"
                        name="email"
                        required
                        placeholder="navn@domaene.dk"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="drawer_phone" className="block text-xs font-bold text-[#39463A] mb-1">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="drawer_phone"
                        name="phone"
                        required
                        placeholder="F.eks. 12 34 56 78"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="drawer_company" className="block text-xs font-semibold text-[#39463A] mb-1">
                      Evt. Firmanavn / CVR (valgfrit)
                    </label>
                    <input
                      type="text"
                      id="drawer_company"
                      name="company"
                      placeholder="Kennel, jægerklub, virksomhed eller CVR"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                    />
                  </div>
                </div>

                {/* Address (Required if delivery) */}
                {formData.delivery_method === 'levering' && (
                  <div className="space-y-3 pt-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#71806A] block">
                      Leveringsadresse
                    </span>
                    <div>
                      <label htmlFor="drawer_address" className="block text-xs font-bold text-[#39463A] mb-1">
                        Gade og husnummer *
                      </label>
                      <input
                        type="text"
                        id="drawer_address"
                        name="address"
                        required={formData.delivery_method === 'levering'}
                        placeholder="F.eks. Stengade 14"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-1">
                        <label htmlFor="drawer_zip" className="block text-xs font-bold text-[#39463A] mb-1">
                          Postnr *
                        </label>
                        <input
                          type="text"
                          id="drawer_zip"
                          name="zip"
                          required={formData.delivery_method === 'levering'}
                          placeholder="7323"
                          value={formData.zip}
                          onChange={handleInputChange}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                        />
                      </div>
                      <div className="col-span-2">
                        <label htmlFor="drawer_city" className="block text-xs font-bold text-[#39463A] mb-1">
                          By *
                        </label>
                        <input
                          type="text"
                          id="drawer_city"
                          name="city"
                          required={formData.delivery_method === 'levering'}
                          placeholder="Give"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes / Bemærkninger */}
                <div className="pt-2">
                  <label htmlFor="drawer_notes" className="block text-xs font-semibold text-[#39463A] mb-1">
                    Bemærkninger til ordren (valgfrit)
                  </label>
                  <textarea
                    id="drawer_notes"
                    name="notes"
                    rows={3}
                    placeholder="Må stilles i carport, foretrukken leveringsdag, spørgsmål til foderet..."
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all resize-none"
                  />
                </div>

                {/* Compact Order mini-summary */}
                <div className="p-3.5 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-xs space-y-1">
                  <div className="flex justify-between font-bold text-[#39463A]">
                    <span>Antal varer i alt:</span>
                    <span>{totalItems} stk.</span>
                  </div>
                  {totalPrice > 0 && (
                    <div className="flex justify-between font-bold text-[#39463A] pt-1 border-t border-[#DED3C0]/60">
                      <span>Vejledende total:</span>
                      <span className="text-[#71806A] font-extrabold">{totalPrice} kr.</span>
                    </div>
                  )}
                  <p className="text-[10px] text-[#39463A]/70 pt-1">
                    Ingen betaling nu. Du modtager faktura eller betalingslink efter vi har godkendt ordren.
                  </p>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 px-6 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                      <span>Indsender bestilling...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                      <span>Send bestilling uden betaling</span>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* STEP 3: ORDER CONFIRMATION / SUCCESS */}
            {step === 'success' && orderReceipt && (
              <div className="py-8 text-center space-y-5">
                <div className="w-16 h-16 rounded-full bg-[#71806A]/15 text-[#71806A] flex items-center justify-center mx-auto text-3xl">
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                </div>

                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                    Tak for din bestilling!
                  </span>
                  <h3 className="text-2xl font-extrabold text-[#39463A] mt-1">
                    Vi har modtaget din ordre
                  </h3>
                  <p className="text-xs text-[#39463A]/80 mt-2 max-w-sm mx-auto leading-relaxed">
                    Vi gennemgår din bestilling og kontakter dig snarest på{' '}
                    <strong className="text-[#39463A]">{orderReceipt.phone}</strong> eller{' '}
                    <strong className="text-[#39463A]">{orderReceipt.email}</strong> for at bekræfte levering og betaling.
                  </p>
                </div>

                {/* Receipt Card */}
                <div className="bg-[#F4EFE5] rounded-2xl p-5 border border-[#DED3C0] text-left text-xs space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-[#DED3C0]">
                    <span className="font-bold text-[#39463A]">Ordrenummer:</span>
                    <span className="font-mono font-bold text-[#71806A] text-sm">
                      {orderReceipt.orderNumber}
                    </span>
                  </div>

                  <div>
                    <span className="font-bold text-[#39463A] block mb-1">Bestilte produkter:</span>
                    <ul className="space-y-1 text-[#39463A]/90">
                      {orderReceipt.items.map((it, idx) => (
                        <li key={idx} className="flex justify-between items-center text-[11px]">
                          <span>
                            {it.quantity}x {it.name} ({it.variant})
                          </span>
                          {it.price ? (
                            <span className="font-semibold">{it.price * it.quantity} kr.</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-2 border-t border-[#DED3C0] flex justify-between font-bold text-[#39463A]">
                    <span>Levering:</span>
                    <span className="text-right text-[11px] font-normal text-[#39463A]/90">
                      {orderReceipt.deliveryAddress}
                    </span>
                  </div>

                  {orderReceipt.totalPrice > 0 && (
                    <div className="flex justify-between font-extrabold text-[#39463A] text-sm pt-1 border-t border-[#DED3C0]">
                      <span>Samlet beløb:</span>
                      <span className="text-[#71806A]">{orderReceipt.totalPrice} kr.</span>
                    </div>
                  )}
                </div>

                {/* Next steps info */}
                <div className="p-3.5 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] text-left text-xs space-y-1">
                  <div className="flex items-center gap-1.5 font-bold text-[#39463A]">
                    <i className="fa-solid fa-clock text-[#71806A]" aria-hidden="true" />
                    <span>Hvad sker der nu?</span>
                  </div>
                  <p className="text-[11px] text-[#39463A]/80 leading-relaxed">
                    1. Vi pakker din bestilling på vores lager i Give.<br />
                    2. Du modtager en bekræftelse samt faktura eller MobilePay-oplysninger.<br />
                    3. Varerne leveres til din adresse eller gøres klar til afhentning efter aftale.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleCloseAndReset}
                    className="w-full py-3 px-6 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer"
                  >
                    Luk og fortsæt med at udforske siden
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer Controls (Only in 'cart' view when items exist) */}
          {step === 'cart' && items.length > 0 && (
            <div className="p-6 bg-[#FCFAF5] border-t border-[#DED3C0] space-y-4">
              {/* Summary line */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-xs text-[#39463A]">
                  <span>Antal produkter:</span>
                  <span className="font-bold">{totalItems} stk.</span>
                </div>
                {totalPrice > 0 && (
                  <div className="flex justify-between items-center text-base font-extrabold text-[#39463A] pt-1">
                    <span>Vejledende total:</span>
                    <span className="text-xl text-[#71806A] font-extrabold">
                      {totalPrice} kr.
                    </span>
                  </div>
                )}
                <div className="text-[10px] text-[#39463A]/70 flex items-center justify-between">
                  <span>Betaling:</span>
                  <span className="font-semibold text-[#71806A]">
                    Ingen kortbetaling – afregnes efterfølgende
                  </span>
                </div>
              </div>

              {/* Checkout CTA */}
              <button
                type="button"
                onClick={handleStartCheckout}
                className="w-full py-3.5 px-6 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md hover:shadow-lg active:scale-98 flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Fortsæt til bestillingsformular</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </button>

              <div className="text-center">
                <Link
                  to="/kurv"
                  onClick={closeCart}
                  className="text-[11px] font-semibold text-[#71806A] hover:text-[#39463A] underline transition-colors"
                >
                  Eller åbn fuld kurv-side
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
